export default {
    /**
     * BookingSource.js contains methods that initialize stepper
     * Handles first user interaction and loads necessary step sequence
     * @see BookingOptions.js
     *
     */
    methods: {
        /**
         * Booking from the orange new lesson button on the calendar
         * Accessible by staff only
         */
        $_source_handleNewBooking() {
            if (!this.bookingSource.isNew) return;
            this.$_options_staffTypeSteps();
            //this.$_options_newLessonSteps();
        },
        /**
         * Booking by directly selecting a time on the calendar
         * Accessible by staff only
         */
        $_source_handleCalendarBooking(event) {
            if (!event) return;
            if (!this.bookingSource.isCalendar) return;

            let assessmentType = this.$_data_getAutoAssessmentType(event);

            let formData = {
                location: event.location,
                type: event.type,
                startDate: event.date.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                }),
                startTime: moment(new Date(event.date)).format("h:mm A"),
                repeating: event.type?.recurring,
                teacher: this.$_data_getAutoTeacher(event),
                instrument: this.$_data_getAutoInstrument(event),
                room: this.$_data_getAutoRoom(event),
                assessmentType: assessmentType,
                duration: this.$_data_getAutoDuration(event, assessmentType),
                online: this.$_data_getAutoOnline(event),
                /*purchaseOption:
                    this.$_data_getPurchaseOptions.length == 1
                        ? this.$_data_getPurchaseOptions[0]
                        : null,*/
            };

            // hack to prevent teacher from booking themselves into someone else's assignment
            if (!this.can("Manage assignments")) {
                if (event.assignment?.user_id !== formData.teacher.id) {
                    event.assignment = null;
                }
            }

            let componentData = {
                assignment: event.assignment ?? null,
                availability: event.assignment?.availability ?? null,
            };

            if (event.type.event) {
                this.$_options_groupEventSteps(
                    event.type,
                    formData,
                    componentData
                );
            } else if (event.type.hold) {
                this.$_options_calendarHoldSteps(
                    event.type,
                    formData,
                    componentData
                );
            } else {
                this.$_options_calendarLessonSteps(
                    event,
                    formData,
                    componentData
                );
            }
        },
        /**
         * Booking by directly drag and drop lesson on calendar
         * Accessible by staff only
         * Teacher/Instrument will respect assignment if it exists
         * Assignment will be removed if teacher changes
         */
        $_source_handleCalendarDragDropRescheduleBooking(event) {
            if (!event) return;
            if (!this.bookingSource.isDragDropReschedule) return;

            const assessmentType = this.$_data_getAutoAssessmentType(event);
            const teacher = this.$_data_getAutoTeacher(event);
            const instrument = this.$_data_getAutoInstrument({
                ...event,
                teacher,
            });

            const formData = {
                location: event.location,
                type: event.type,
                startDate: event.date.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                }),
                startTime: moment(new Date(event.date)).format("h:mm A"),
                repeating: false,
                teacher: teacher,
                instrument: instrument,
                room: this.$_data_getAutoRoom(event),
                assessmentType: assessmentType,
                duration: this.$_data_getAutoDuration(event, assessmentType),
                online: this.$_data_getAutoOnline(event),

                student: event.student,
                credit: event.credit,
                purchaseOption: event.purchaseOption,
                billingUser: event.purchaseOption.user ?? null,
            };

            // hack to prevent teacher from booking themselves into someone else's assignment
            if (!this.can("Manage assignments")) {
                if (event.assignment?.user_id !== formData.teacher.id) {
                    event.assignment = null;
                }
            }

            let componentData = {
                assignment: event.assignment ?? null,
                availability: event.assignment?.availability ?? null,
            };

            this.$_options_dragDropRescheduleSteps(
                event,
                formData,
                componentData
            );
        },
        /**
         * @see LessonAgenda.vue
         * @see Schedule.vue
         * Reschedule button from the calendar or lessonAgenda
         * Accessible by staff only
         */
        $_source_handleRescheduleBooking(event) {
            if (!event) return;
            if (!this.bookingSource.isReschedule) return;

            let assessmentType = this.$_data_getAutoAssessmentType(event);

            let selectOption = this.$_data_getAutoPurchaseOption(
                event,
                event.student,
                assessmentType
            );

            let formData = {
                location: event.location,
                type: event.type,
                student: event.student,
                repeating: false,
                credit: event,
                teacher: this.$_data_getAutoTeacher(null),
                duration: event.duration,
                assessmentType: assessmentType,
                purchaseOption: selectOption,
                billingUser:
                    this.$_data_getAutoBillingUser(
                        event.student,
                        selectOption
                    ) ?? null,
                //instrument: this.$_data_getAutoInstrument(event),
                templateEvent: this.event.group_event,
            };

            if (event.group_event) {
                formData.online = false;
                formData.capacity = event.group_event.capacity;

                this.$_options_clientRescheduleGroupEventSteps(
                    event.type,
                    formData,
                    {},
                    0
                );
            } else {
                this.$_options_clientRescheduleLessonSteps(
                    event,
                    formData,
                    {},
                    0
                );
            }
        },
        /**
         * @see MySchedule.vue
         * @see LessonPanels.vue
         * Reschedule button from lesson panels
         * Accessible by clients only
         * Credit will be the whole event, since it was directly selected
         */
        $_source_handleMyScheduleBooking(event) {
            if (!event) return;
            if (!this.bookingSource.isMySchedule) return;

            let assessmentType = this.$_data_getAutoAssessmentType(event);

            let selectOption = this.$_data_getAutoPurchaseOption(
                event,
                event.student,
                assessmentType
            );

            let formData = {
                credit: event,
                purchaseOption: selectOption,
                billingUser:
                    this.$_data_getAutoBillingUser(
                        event.student,
                        selectOption
                    ) ?? null,
                type: event.type,
                student: event.student,
                repeating: false,
                assessmentType: assessmentType,
                online: this.$_data_getAutoOnline(event),
                isSelfServe: true,
            };

            if (
                event.type.name == "Event" &&
                event.group_event?.can_reschedule &&
                !event.group_event?.can_exchange_passes
            ) {
                this.$_options_clientRescheduleClassSteps(
                    event,
                    formData,
                    {},
                    0
                );
            } else {
                this.$_options_clientDirectRescheduleSteps(event, formData);
            }
        },
        /**
         * @see MySchedule.vue
         * @see StudentBookingPanel.vue
         * Booking from pinned panel in mySchedule
         * Accessible by clients only
         * Don't get auto credit here, client should select the missed lesson
         */
        async $_source_handleSelfServeBooking(event) {
            if (!event) return;
            if (!this.bookingSource.isSelfServe) return;

            let student =
                this.$page.props.relationships.length < 2
                    ? this.$page.props.auth.user
                    : null;

            await this.$_apis_getCancelledLessons(
                event.type?.reschedule,
                student
            );

            let selectOption = null;

            let billingUser =
                this.$_data_getAutoBillingUser(student, selectOption) ?? null;

            if (!event.type?.reschedule) {
                selectOption = this.$_data_getAutoPurchaseOption(
                    event,
                    student
                );
            }

            let assessmentType = this.$_data_getAutoAssessmentType(event);

            let formData = {
                type: event.type,
                repeating: false,
                student: student,
                assessmentType: assessmentType,
                purchaseOption: selectOption,
                duration: selectOption?.duration ?? null,
                billingUser: billingUser,
                online: this.$_data_getAutoOnline(event),
                isSelfServe: true,
                //email: this.$page.props.auth.user.email,
            };

            this.$_data_checkRescheduleValues(
                event.type,
                selectOption?.duration ?? null,
                student
            );

            if (student && selectOption) {
                this.$_options_clientRescheduleLessonSteps(
                    event,
                    formData,
                    {},
                    0
                );
            } else {
                /*if (event.type?.assessment) {
                    this.$_options_clientAssessmentSteps(event, formData, {});
                    return;
                }*/
                this.$_options_selfServeSteps(event, formData);
            }
        },

        /**
         * @see GetStarted.vue
         * Booking from GetStarted form
         * Accessible by guests only
         */
        $_source_handleGuestSessionBooking(event) {
            if (!event) return;
            if (!this.bookingSource.isGuestSession) return;
            if (event.lesson) return;

            let selectOption = this.$_data_getAutoPurchaseOption(event, null);
            let assessmentType = this.$_data_getAutoAssessmentType(event);

            let formData = {
                type: event.type,
                repeating: false,
                purchaseOption: selectOption,
                duration: selectOption.duration,
                assessmentType: assessmentType,
                //location: this.event.guest.selectedLocation,
                student: event.student,
                billingUser: this.event.guest,
                isSelfServe: true,
                //instrument: this.event.guest.selectedInstrument,
            };

            this.$_options_guestAssessmentSteps(event, formData, {});
        },

        /**
         * @see GetStarted.vue
         * Booking from GetStarted form
         * Accessible by Prospects only
         */
        $_source_completeGuestSessionBooking(event) {
            if (!event) return;
            if (!this.bookingSource.isGuestSession) return;
            if (!this.bookingSource.isGuestSession) return;
            if (!event.lesson) return;
            //if (!event.type.pending || !event.purchaseOption) return;

            let selectOption = this.$_data_getAutoPurchaseOption(event, null);
            let assessmentType = this.$_data_getAutoAssessmentType(event);

            let formData = {
                type: event.type,
                repeating: false,
                purchaseOption: selectOption,
                assessmentType: assessmentType,

                location: event.location,
                type: event.type,
                //student: event.student,
                teacher: event.teacher,
                duration: event.duration,
                assessmentType: assessmentType,
                purchaseOption: selectOption,
                instrument: this.$_data_getAutoInstrument(event),
                isSelfServe: true,
            };

            this.$_options_convertGuestSteps(event, formData, {});
        },

        /**
         * @see GetStarted.vue
         * Booking from GetStarted form
         * Accessible by guests only
         */
        $_source_handleConvertGuestAccount(event) {
            if (!this.bookingSource.isConvertGuestAccount) return;
            let formData = {
                type: event.type,
                repeating: false,
                duration: 30,
                assessmentType: this.$_data_getAutoAssessmentType(event),
                isSelfServe: true,
            };

            this.$_options_convertGuestSteps(event, formData, {});
        },
    },
};
